import { useState, useCallback } from 'react';
import { useAuth } from '../../../core/providers/AuthProvider';
import { useCases } from '../../../core/di/useCases';
import { logger } from '../../../shared/utils/logger';
import { useNotification } from '../../../shared/hooks/useNotification';

export const useParticipation = (post, initialParticipating = false) => {
  const [isParticipating, setIsParticipating] = useState(initialParticipating);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { user } = useAuth();
  const notification = useNotification('posts');

  const toggleParticipation = useCallback(async () => {
    if (!user?.uid || !post?.id) {
      logger.error('Missing user or post data', { userId: user?.uid, postId: post?.id });
      setError('Unable to join/leave activity: missing data');
      notification.error('toast.participation_error');
      return { success: false, error: 'Missing required data' };
    }
    
    setLoading(true);
    setError(null);

    try {
      const participantData = {
        uid: user.uid,
        name: user.displayName || '',
        photoURL: user.photoURL || null,
        displayName: user.displayName || ''
      };

      let existingParticipantDetails = null;
      const participantsDetails = post.activity?.participantsDetails || post.participantsDetails || [];
      
      if (isParticipating) {
        existingParticipantDetails = participantsDetails.find(p => p.uid === user.uid) || participantData;
      }

      const result = await (isParticipating 
        ? useCases.leaveActivity.execute(post.id, user.uid, existingParticipantDetails)
        : useCases.joinActivity.execute({
            postId: post.id,
            userId: user.uid,
            participantData,
            currentParticipants: post.activity?.participants || post.participants || [],
            currentDetails: post.activity?.participantsDetails || post.participantsDetails || []
          })
      );

      if (result.success) {
        setIsParticipating(!isParticipating);
        notification.success(
          isParticipating 
            ? 'toast.left_activity_success'
            : 'toast.joined_activity_success'
        );
      } else {
        setError(result.error);
        notification.error(
          isParticipating 
            ? 'toast.left_activity_error'
            : 'toast.joined_activity_error'
        );
      }
      
      return result;
    } catch (err) {
      logger.error('Error in toggleParticipation:', err);
      setError(err.message || 'Failed to toggle participation');
      notification.error(
        isParticipating 
          ? 'toast.left_activity_error'
          : 'toast.joined_activity_error'
      );
      return { success: false, error: err.message };
    } finally {
      setLoading(false);
    }
  }, [isParticipating, post, user, notification]);

  return {
    isParticipating,
    loading,
    error,
    toggleParticipation
  };
}; 